import { useEffect, useCallback } from 'react'
import { useIsVirtualMarketer } from './useIsVirtualMarketer'
import { useIsSystemRedesign } from './useIsSystemRedesign'
import { useRootStore } from 'stores/RootStore'
import { useLocation } from 'react-router-dom'
import { useIsPioneerStarterOrGrowth } from './useIsPioneerStarterOrGrowth'
import useResponsiveDesign from './useResponsiveDesign'
import { useCurrentWorkspace } from './useCurrentWorkspace'

export const useAutopilotProvider = () => {
  const { featureFlags } = useRootStore()
  const location = useLocation()
  const currentWorkspace = useCurrentWorkspace()
  const isVirtualMarketer = useIsVirtualMarketer()
  const isSystemRedesign = useIsSystemRedesign()
  const { isMobile } = useResponsiveDesign()

  const isPioneer = useIsPioneerStarterOrGrowth() === 'pioneer'

  const isOnVirtualMarketerOnboarding = location.pathname.includes('/virtual-marketer-onboarding')
  const isOnRegistration = location.pathname.includes('/signup')
  const isOnLogin = location.pathname.includes('/login')
  const isOnboardingPath = isOnVirtualMarketerOnboarding || isOnRegistration || isOnLogin

  // Pioneer users should not see the onboarding redesign once they start the onboarding flow from the website input step
  const dontApplyRedesign = isPioneer && !isOnLogin && !isOnRegistration

  // Set onboarding redesign and autopilot system redesign attributes ON if the onboarding redesign feature flag is active
  const setOnboardingRedesignAttributes = useCallback(
    (isActive: boolean) => {
      if (isOnboardingPath) {
        document.documentElement.setAttribute('data-autopilot-system-redesign', isActive ? 'on' : 'off')
      }
    },
    [isOnboardingPath],
  )

  // Load feature flags if they are not already loaded
  const loadFeatureFlags = useCallback(async () => {
    if (!featureFlags.ready) await featureFlags.load()

    // Keep redesign on for login/signup/onboarding even when viewport is under 720px
    const isActive = !!isOnboardingPath && !dontApplyRedesign

    setOnboardingRedesignAttributes(isActive)
  }, [featureFlags, setOnboardingRedesignAttributes, isOnboardingPath, dontApplyRedesign])

  useEffect(() => {
    loadFeatureFlags()
  }, [loadFeatureFlags]) // eslint-disable-line react-hooks/exhaustive-deps

  // Set the autopilot system redesign attribute after login, signup and onboarding for AP workspaces
  useEffect(() => {
    if (isOnboardingPath || !currentWorkspace) return

    if ((!!currentWorkspace && !isVirtualMarketer) || isMobile) {
      document.documentElement.setAttribute('data-autopilot-system-redesign', 'off')

      return
    }

    if (isSystemRedesign) {
      document.documentElement.setAttribute('data-autopilot-system-redesign', 'on')
    } else {
      document.documentElement.setAttribute('data-autopilot-system-redesign', 'off')
    }
  }, [isVirtualMarketer, isSystemRedesign, isOnboardingPath, isMobile, currentWorkspace])
}
